import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSGPQuery, useCreateSGPCompanyMutation, useUpdateSGPCompanyMutation } from 'api/main';
import SGPCompanyForm from './SGPCompanyForm';
import { Box, Typography } from '@mui/material';
import { TunnelBox } from '../tunnel/common';
import { validateSchema } from 'utils/helpers';
import SGPFormSchema from './SGPFormSchema';
import { texts } from 'utils/languages/fr';
import { useGetDocURLById } from 'utils/hooks';
import { ActionButton, CancelButton, Loader } from 'components';

const SGPCompany = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const { data, isLoading } = useGetSGPQuery(id || '');

    const [form, setForm] = useState({
        name: '',
        logo: null,
        signatureImage: null,
        logoUrl: '',
        signatureImageUrl: '',
        currentAddress: {
            address: '',
            city: '',
            zipCode: '',
            country: '',
        },
        rcsNumber: '',
        rcsRegistrationAddress: '',
        amfData: '',
        legalRepresentativeName: '',
        legalRepresentativeFunction: '',
        contactEmail: '',
        documentsFooterText: '',
        distributorBillEmail: '',
    });
    const [errors, setErrors] = useState({});

    const [
        createSGPCompany,
        { isLoading: createLoader, isSuccess: createSuccess, error: createError },
    ] = useCreateSGPCompanyMutation();

    const [updateSGPCompany, { isLoading: updateLoader, isSuccess: updateSuccess }] =
        useUpdateSGPCompanyMutation();

    const isEditMode = Boolean(id);

    useEffect(() => {
        if (data) {
            setForm(prevState => ({
                ...prevState,
                ...data.data,
                logoUrl: data.data.logo ? data.data.logo.url : '',
                signatureImageUrl: data.data.signatureImage ? data.data.signatureImage.url : '',
                currentAddress: data.data.addressLocation
                    ? data.data.addressLocation
                    : {
                          address: '',
                          city: '',
                          zipCode: '',
                          country: '',
                      },
            }));
        }
    }, [data]);

    const { src: logoUrl } = useGetDocURLById(form.logo ? form.logo.id : null);
    const { src: signatureUrl } = useGetDocURLById(
        form.signatureImage ? form.signatureImage.id : null
    );

    useEffect(() => {
        if (logoUrl) {
            setForm(prevState => ({ ...prevState, logoUrl }));
        }
    }, [logoUrl]);

    useEffect(() => {
        if (signatureUrl) {
            setForm(prevState => ({ ...prevState, signatureImageUrl: signatureUrl }));
        }
    }, [signatureUrl]);

    const handleChange = key => e => {
        const { value } = e.target;
        setForm(oldValue => ({ ...oldValue, [key]: value }));
        setErrors(prevState => ({ ...prevState, [key]: '' }));
    };

    const handleAddressChange = address => {
        setForm(prevState => ({
            ...prevState,
            currentAddress: { ...prevState.currentAddress, ...address },
        }));
        setErrors(prevState => ({
            ...prevState,
            currentAddress: {
                ...prevState.currentAddress,
                address: '',
                city: '',
                zipCode: '',
                country: '',
            },
        }));
    };

    const handleLogoChange = async e => {
        const file = e.target.files[0];
        if (file) {
            const fileURL = URL.createObjectURL(file);
            setForm(prevState => ({ ...prevState, logo: file, logoUrl: fileURL }));
        }
    };

    const handleRemoveLogo = () => {
        setForm(prevState => ({ ...prevState, logo: null, logoUrl: '' }));
    };

    const handleSignatureChange = async e => {
        const file = e.target.files[0];
        if (file) {
            const fileURL = URL.createObjectURL(file);
            setForm(prevState => ({
                ...prevState,
                signatureImage: file,
                signatureImageUrl: fileURL,
            }));
        }
    };

    const handleRemoveSignature = () => {
        setForm(prevState => ({ ...prevState, signatureImage: null, signatureImageUrl: '' }));
    };

    const handleSubmit = useCallback(() => {
        const trimmedForm = {
            ...form,
            name: form.name.trim(),
            rcsNumber: form.rcsNumber.trim(),
            rcsRegistrationAddress: form.rcsRegistrationAddress.trim(),
            amfData: form.amfData.trim(),
            legalRepresentativeName: form.legalRepresentativeName.trim(),
            legalRepresentativeFunction: form.legalRepresentativeFunction.trim(),
            contactEmail: form.contactEmail.trim(),
            documentsFooterText: form.documentsFooterText.trim(),
            distributorBillEmail: form.distributorBillEmail.trim(),
            currentAddress: {
                ...form.currentAddress,
                address: form.currentAddress.address.trim(),
                city: form.currentAddress.city.trim(),
                zipCode: form.currentAddress.zipCode.trim(),
                country: form.currentAddress.country.trim(),
            },
        };
        const { valid, newErrors } = validateSchema(SGPFormSchema, trimmedForm);
        if (valid) {
            setErrors({
                logo: '',
                name: '',
                rcsNumber: '',
                rcsRegistrationAddress: '',
                amfData: '',
                legalRepresentativeName: '',
                legalRepresentativeFunction: '',
                contactEmail: '',
                documentsFooterText: '',
                signatureImage: '',
                distributorBillEmail: '',
                currentAddress: {
                    address: '',
                    city: '',
                    zipCode: '',
                    country: '',
                },
            });
            if (isEditMode) {
                updateSGPCompany(trimmedForm);
            } else {
                createSGPCompany(trimmedForm);
            }
        } else {
            setErrors(newErrors);
        }
    }, [createSGPCompany, updateSGPCompany, form, isEditMode]);

    const goBack = useCallback(() => {
        navigate('/sgp-companies');
    }, [navigate]);

    useEffect(() => {
        if (createSuccess || updateSuccess) {
            goBack();
        }
    }, [createSuccess, updateSuccess, goBack]);

    useEffect(() => {
        if (createError?.data['hydra:description'] === 'name: sgp.name.exists') {
            setErrors(prevState => ({ ...prevState, name: texts.SGP_NAME_INVALID }));
        }
    }, [createError]);

    return (
        <Loader
            loading={isLoading || createLoader || updateLoader}
            size={25}
            marginTop={0}
            color="inherit"
        >
            <Typography variant="h5" align="left" gutterBottom>
                {isEditMode ? texts.SGP_EDIT_PROVIDER : texts.SGP_CREATE_PROVIDER}
            </Typography>
            <TunnelBox sx={{ marginTop: '30px' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: {
                            xl: '50%',
                            lg: '80%',
                            md: '100%',
                        },
                        margin: '0 auto',
                    }}
                >
                    <SGPCompanyForm
                        form={form}
                        handleChange={handleChange}
                        handleAddressChange={handleAddressChange}
                        errors={errors}
                        handleLogoChange={handleLogoChange}
                        handleRemoveLogo={handleRemoveLogo}
                        handleSignatureChange={handleSignatureChange}
                        handleRemoveSignature={handleRemoveSignature}
                    />
                </Box>
            </TunnelBox>
            <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
                <CancelButton disabled={createLoader} onClick={goBack}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={createLoader}
                    loading={createLoader}
                >
                    {texts.SAVE}
                </ActionButton>
            </Box>
        </Loader>
    );
};

export default SGPCompany;
